import React, { useState } from 'react';
import {
  Paper,
  Tabs,
  Tab,
  Badge,
  Box,
  Tooltip,
  type SxProps,
  type Theme,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useCANvisionContext } from '../../context/useCANvisionContext';
import EventsTab from '../events/EventsTab';
import { IncidentsTab } from '../incidents/IncidentsTab';
import {
  ROUTE_BASE,
  ROUTE_CANVISION_EVENTS,
  ROUTE_CANVISION_INCIDENTS,
} from '../../../../../constants/routerPath';

// ---------------------------------------------------------------------------
// Tab index type
// ---------------------------------------------------------------------------

type TabIndex = 0 | 1;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface EventsIncidentsTabsProps {
  sx?: SxProps<Theme>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * 50/50 tabbed container that switches between the Events and Incidents panels.
 *
 * - Tab 0: "Events" label with a badge showing the count of new events.
 * - Tab 1: "Incidents" label with a badge showing the count of active incidents.
 *
 * Each tab occupies exactly 50% of the header width via `variant="fullWidth"`
 * and `sx={{ flex: 1 }}` on each `<Tab>`.
 *
 * The `EventsTab` and `IncidentsTab` components are imported from their
 * respective feature directories and rendered as the tab panel content.
 */
export function EventsIncidentsTabs({ sx }: EventsIncidentsTabsProps) {
  const [activeTab, setActiveTab] = useState<TabIndex>(0);
  const { dashboardStats, refetchDashboard, cameras, sites } = useCANvisionContext();

  // Derive siteIds for IncidentsTab
  const siteIds = sites.map((s) => s.id);

  // Derive counts from dashboard stats for badge display
  const eventCount = dashboardStats?.recent_events?.new ?? 0;
  const incidentCount = dashboardStats?.incidents?.active ?? 0;

  // Tab content components — wired to real Phase 2 components
  const EventsContent = EventsTab;
  const IncidentsContent = IncidentsTab;

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        ...sx,
      }}
    >
      {/* ---- Tab headers ---- */}
      <Tabs
        value={activeTab}
        onChange={(_, newValue: TabIndex) => setActiveTab(newValue)}
        variant="fullWidth"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: { xs: 40, sm: 48 },
        }}
      >
        <Tab
          label={
            <Badge
              badgeContent={eventCount}
              color="warning"
              max={999}
              sx={{ '& .MuiBadge-badge': { right: -12, top: 2 } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <NotificationsActiveIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
                <Box component="span" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  Events
                </Box>
                <Tooltip title="Open in new tab">
                  <Box
                    component="span"
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(ROUTE_BASE + ROUTE_CANVISION_EVENTS, '_blank');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.stopPropagation();
                        window.open(ROUTE_BASE + ROUTE_CANVISION_EVENTS, '_blank');
                      }
                    }}
                    sx={{ cursor: 'pointer', display: 'inline-flex', ml: 0.5, '&:hover': { opacity: 0.7 } }}
                  >
                    <OpenInNewIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
                  </Box>
                </Tooltip>
              </Box>
            </Badge>
          }
          sx={{ flex: 1, textTransform: 'none', fontWeight: 600, minHeight: { xs: 40, sm: 48 }, px: { xs: 1, sm: 2 } }}
          id="events-incidents-tab-0"
          aria-controls="events-incidents-tabpanel-0"
        />
        <Tab
          label={
            <Badge
              badgeContent={incidentCount}
              color="error"
              max={999}
              sx={{ '& .MuiBadge-badge': { right: -12, top: 2 } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ReportProblemIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
                <Box component="span" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  Incidents
                </Box>
                <Tooltip title="Open in new tab">
                  <Box
                    component="span"
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(ROUTE_BASE + ROUTE_CANVISION_INCIDENTS, '_blank');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.stopPropagation();
                        window.open(ROUTE_BASE + ROUTE_CANVISION_INCIDENTS, '_blank');
                      }
                    }}
                    sx={{ cursor: 'pointer', display: 'inline-flex', ml: 0.5, '&:hover': { opacity: 0.7 } }}
                  >
                    <OpenInNewIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
                  </Box>
                </Tooltip>
              </Box>
            </Badge>
          }
          sx={{ flex: 1, textTransform: 'none', fontWeight: 600, minHeight: { xs: 40, sm: 48 }, px: { xs: 1, sm: 2 } }}
          id="events-incidents-tab-1"
          aria-controls="events-incidents-tabpanel-1"
        />
      </Tabs>

      {/* ---- Tab panels ---- */}
      <Box
        role="tabpanel"
        id="events-incidents-tabpanel-0"
        aria-labelledby="events-incidents-tab-0"
        hidden={activeTab !== 0}
      >
        {activeTab === 0 && <EventsContent onEventsChanged={refetchDashboard} />}
      </Box>

      <Box
        role="tabpanel"
        id="events-incidents-tabpanel-1"
        aria-labelledby="events-incidents-tab-1"
        hidden={activeTab !== 1}
      >
        {activeTab === 1 && (
          <IncidentsContent
            cameras={cameras}
            siteIds={siteIds}
            onRefresh={refetchDashboard}
          />
        )}
      </Box>
    </Paper>
  );
}

export default EventsIncidentsTabs;
